// ============================================================
// MARKER STATUS - what a marker says about the combatant standing there.
//
// The map has always known where everyone is and nothing about how they
// are doing: to learn that a marine is at 4 wounds and on fire you had
// to look away from the map, which is the one place the tactical picture
// is not. The only status a marker carried was the `⚠` movement note,
// under a comment saying "a marker has room for one".
//
// EVERY NUMBER HERE IS BORROWED, NONE IS DERIVED. `vitalsOf` decides
// which pair of numbers a combatant's health is, `healthBand` decides
// what they mean, `condSeverityOrder` decides which conditions win the
// space. That is deliberate to the point of pedantry: `healthRamp.ts`
// exists because four call sites each computed their own ratio and three
// disagreed, and a fifth on the map would be the same bug with a better
// view.
//
// Pure. Zero React dependency.
// ============================================================
import { healthBand, healthColor, vitalsOf } from '../healthRamp';
import type { HealthBand } from '../healthRamp';
import { condSeverityOrder, condType, hasConditionIcon } from '../conditions';
import type { Target } from '../../types';

export type MarkerStatusLevel = 'off' | 'minimal' | 'full';

export interface MarkerStatusEntry {
  key: MarkerStatusLevel;
  label: string;
  hint: string;
}

/**
 * How much a marker is allowed to say. MINIMAL is the default.
 *
 * Three steps rather than a switch, because the two useful answers are
 * not "on" and "off": a fight wants the ring and the conditions, and
 * only the person taking the shot wants the magazine.
 */
export const MARKER_STATUS_LEVELS: MarkerStatusEntry[] = [
  { key: 'off', label: 'No status', hint: 'Markers say only who and where' },
  { key: 'minimal', label: 'Status', hint: 'A wound ring and condition marks on every marker' },
  { key: 'full', label: 'Status + numbers', hint: 'Also the exact wounds, and what is left in your magazine' },
];

const LEVEL_KEYS = new Set(MARKER_STATUS_LEVELS.map((l) => l.key));

/** A stored preference is untrusted input - a later build may have written a level this one lacks. */
export function isMarkerStatusLevel(value: unknown): value is MarkerStatusLevel {
  return typeof value === 'string' && LEVEL_KEYS.has(value as MarkerStatusLevel);
}

/**
 * How many condition chips fit beside a marker.
 *
 * A judgement, exported so it is findable: a fight with fire, blood loss
 * and a crippled arm already fills it.
 */
export const MAX_CHIPS = 3;

interface AmmoStatus {
  current: number;
  max: number;
  /** 0..1 - how much of the wedge is filled. */
  fraction: number;
  /** A CSS variable, never a hex. */
  colour: string;
}

export interface MarkerStatus {
  band: HealthBand;
  /** A CSS variable, never a hex - see `healthRamp`. */
  colour: string;
  /** 0..1, the arc's sweep. */
  fraction: number;
  shown: number;
  total: number;
  isHorde: boolean;
  /** Condition codes, worst first, capped at MAX_CHIPS and all drawable. */
  chips: string[];
  /** How many conditions did not fit. */
  overflow: number;
  dead: boolean;
  ammo?: AmmoStatus;
  /** The one-line readout, at `full` only. */
  numbers?: string;
}

export interface MarkerStatusOptions {
  level: MarkerStatusLevel;
  /** How many condition chips before "+n": MAX_CHIPS, or the display's four (plan map-display, C). */
  maxChips?: number;
  /**
   * The live magazine of the weapon in hand.
   *
   * Only ever the actor's: `selectedSlot` belongs to the attack panel,
   * so no other combatant has a "current weapon" to report.
   */
  ammo?: { current: number; max: number } | null;
}

/** Worst first, drawable only, capped. */
function chipsOf(conditions: string[], max: number): { chips: string[]; overflow: number } {
  const drawable = conditions.filter(hasConditionIcon);
  // `sort` is stable, so equal severities keep the order they were
  // applied in - which is the order the conditions list already shows.
  const ranked = [...drawable].sort((a, b) => condSeverityOrder(a) - condSeverityOrder(b));
  return { chips: ranked.slice(0, max), overflow: Math.max(0, ranked.length - max) };
}

/** The magazine as a fraction, or nothing at all when there is no magazine. */
function ammoOf(ammo: { current: number; max: number } | null | undefined): AmmoStatus | undefined {
  // A melee weapon projects as {current: 0, max: 0}. Drawn naively that
  // is an empty gauge on every actor holding a chainsword - which reads
  // as "out of ammo" rather than "there is no ammo to be out of".
  if (!ammo || !(ammo.max > 0)) return undefined;
  return {
    current: ammo.current,
    max: ammo.max,
    fraction: Math.min(1, Math.max(0, ammo.current / ammo.max)),
    // THE SAME RAMP THE WOUNDS USE, deliberately. `healthRamp.ts` exists
    // because four call sites each invented their own thresholds and
    // three disagreed; "how full is this, out of its maximum" is the
    // identical question here, and a second ramp for ammunition would be
    // that mistake with a new name. The two gauges are told apart by
    // WHERE they are - a quarter turn on an outer ring against a whole
    // circle on an inner one - not by their colour.
    colour: healthColor(ammo.current, ammo.max),
  };
}

/** The one line above the body, at `full`. Undefined when it would say nothing. */
function numbersOf(v: { shown: number; total: number; isHorde: boolean }, ammo?: AmmoStatus): string | undefined {
  const parts: string[] = [];
  // A horde's magnitude is already printed on the ground by HordeAreas,
  // dead centre of where this ring is drawn. Saying it twice is worse
  // than not saying it.
  if (!v.isHorde && v.total > 0) parts.push(`${v.shown}/${v.total}`);
  if (ammo) parts.push(`${ammo.current}/${ammo.max}`);
  return parts.length ? parts.join(' · ') : undefined;
}

/** What this marker should show, or null when it should show nothing. */
export function markerStatusOf(target: Target, opts: MarkerStatusOptions): MarkerStatus | null {
  if (opts.level === 'off') return null;
  const v = vitalsOf(target);
  const conditions = target.conditions || [];
  const { chips, overflow } = chipsOf(conditions, opts.maxChips ?? MAX_CHIPS);
  // AT EVERY LEVEL THAT SHOWS ANYTHING (v8.6.0). It used to be `full`
  // only, which meant that in ordinary play - the default is `minimal` -
  // the magazine was never on screen at all and the feature read as
  // missing. The wedge carries no text, so there is nothing about it
  // that belongs behind the level that adds text.
  const ammo = ammoOf(opts.ammo);
  return {
    band: healthBand(v.shown, v.total),
    colour: healthColor(v.shown, v.total),
    // Clamped at zero: below it is critical severity, not a negative
    // ring. The arc has run out and the number carries the rest.
    fraction: v.total > 0 ? Math.min(1, Math.max(0, v.shown / v.total)) : 0,
    shown: v.shown,
    total: v.total,
    isHorde: v.isHorde,
    chips,
    overflow,
    dead: conditions.some((c) => condType(c) === 'K'),
    ...(ammo ? { ammo } : {}),
    ...(opts.level === 'full' ? { numbers: numbersOf(v, ammo) } : {}),
  };
}
